import { lazy, Suspense } from "react"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import MainLayout from "../layouts/MainLayout"
import { ThemeProvider, CssBaseline, Box, Skeleton } from "@mui/material"
import theme from "../theme/theme"
import "../styles/global.css"

import TenantGuard from "../components/guards/tenantGuard"
import StartupRedirect from "../components/system/startupRedirect"
import TenantRouter from "./TenantRouter"
import SessionExpiredModal from "../components/Dialog/SessionExpiredModal"
import GenericErrorPage from "../components/shared/GenericErrorPage"

const HomePage = lazy(() => import("../pages/Home/HomePage"))
const LoginPage = lazy(() => import("../pages/LoginPages/LoginPage"))
const SystemAdminLoginPage = lazy(
  () => import("../pages/LoginPages/SystemAdminLoginPage"),
)
const AccountManagementPage = lazy(
  () =>
    import("../pages/Management/SystemAdmin/AccountManagement/AccountManagementPage"),
)
const VerifyEmail = lazy(() => import("../pages/VerifyEmail"))
const ForgotPassword = lazy(
  () => import("../pages/ForgotPassword/ForgotPassword"),
)
const ResetPassword = lazy(
  () => import("../pages/ForgotPassword/ResetPassword"),
)
const PasswordChanged = lazy(
  () => import("../pages/ForgotPassword/PasswordChanged"),
)
const Dashboard = lazy(() => import("../pages/Dashboard/dashboard"))

const CollapsibleDrawerPage = lazy(
  () => import("../pages/Management/PartnerDashboard/CollapsibleDrawerPage"),
)

const MobileCollapsibleDrawerPage = lazy(
  () =>
    import("../pages/Management/PartnerDashboard/MobileCollapsibleDrawerPage"),
)

const GenericBookingPage = lazy(
  () => import("../pages/Booking/GenericBookingPage"),
)

const BookingDetailPage = lazy(
  () => import("../pages/Dashboard/DetailPages/BookingDetailPage"),
)
const TermsAndConditions = lazy(
  () => import("../pages/Legal/TermsAndConditions"),
)
const PrivacyPolicy = lazy(() => import("../pages/Legal/PrivacyPolicy"))
const NotFound = () => <div style={{ padding: 40 }}>404 - Page Not Found</div>
const RouteSkeleton = () => (
  <Box
    sx={{
      minHeight: "100vh",
      px: { xs: 2, md: 5 },
      py: { xs: 3, md: 4 },
      bgcolor: "background.default",
    }}
  >
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      <Skeleton variant="text" width={240} height={48} />
      <Skeleton variant="text" width="45%" height={28} sx={{ mb: 3 }} />

      <Skeleton
        variant="rounded"
        height={180}
        sx={{ borderRadius: 3, mb: 3 }}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
      </Box>
    </Box>
  </Box>
)

export default function AppRouter() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <StartupRedirect />
        <SessionExpiredModal />
        <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            <Route
              path="/"
              element={
                <MainLayout>
                  <HomePage />
                </MainLayout>
              }
            />
            <Route
              path="/home"
              element={
                <MainLayout>
                  <HomePage />
                </MainLayout>
              }
            />
            <Route
              path="/dashboard"
              element={
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              }
            />
            <Route
              path="/management-dashboard"
              element={<CollapsibleDrawerPage />}
            />
            <Route
              path="/mobile-management-dashboard"
              element={<MobileCollapsibleDrawerPage />}
            />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/system-admin-login"
              element={<SystemAdminLoginPage />}
            />
            <Route
              path="/client-management"
              element={<AccountManagementPage />}
            />
            <Route path="/password-changed" element={<PasswordChanged />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/tenant" element={<TenantRouter />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/booking-detail"
              element={
                <MainLayout>
                  <BookingDetailPage />
                </MainLayout>
              }
            />

            <Route
              path="/booking"
              element={
                <TenantGuard>
                  <GenericBookingPage />
                </TenantGuard>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/unauthorized"
              element={
                <GenericErrorPage
                  title="Something went wrong"
                  message="We couldn't load this page, please try again"
                  buttonLabel="Go Back"
                  buttonTo="/home"
                />
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  )
}
